<template>
  <div class="photo-capture-modal" v-if="show">
    <div class="modal-backdrop" @click="$emit('close')"></div>
    <div class="modal-content">
      <div class="modal-header">
        <h5>{{ title }}</h5>
        <button @click="$emit('close')" class="close-btn">
          <i class="material-icons">close</i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Mode sélection ou capture -->
        <div v-if="!showCamera && !showCropper" class="mode-selector">
          <button @click="startCamera" class="btn btn-primary mode-btn">
            <i class="material-icons">camera_alt</i>
            <span>Utiliser la caméra</span>
          </button>
          <button @click="triggerFileInput" class="btn btn-secondary mode-btn">
            <i class="material-icons">folder</i>
            <span>Sélectionner une image</span>
          </button>
          <input 
            type="file" 
            ref="fileInput" 
            style="display: none" 
            @change="handleFileSelect" 
            accept="image/*"
          >
        </div>
        
        <!-- Interface caméra -->
        <div v-if="showCamera" class="camera-container">
          <div v-if="!cameraCaptured">
            <video ref="video" autoplay playsinline></video>
          </div>
          <div v-else>
            <canvas ref="canvas" class="captured-preview"></canvas>
          </div>
          
          <div class="camera-controls">
            <button v-if="!cameraCaptured" @click="capturePhoto" class="btn btn-primary">
              <i class="material-icons">photo_camera</i> Capturer
            </button>
            <button v-if="cameraCaptured" @click="acceptCaptured" class="btn btn-success">
              <i class="material-icons">check</i> Accepter
            </button>
            <button v-if="cameraCaptured" @click="retakePhoto" class="btn btn-secondary">
              <i class="material-icons">refresh</i> Reprendre
            </button>
            <button v-if="!cameraCaptured" @click="cancelCamera" class="btn btn-outline-secondary">
              <i class="material-icons">arrow_back</i> Retour
            </button>
          </div>
        </div>
        
        <!-- Interface de rognage d'image -->
        <div class="cropper-wrapper" v-if="showCropper && selectedImage">
          <div class="img-container">
            <img ref="cropperImage" :src="selectedImage" alt="Image à rogner" style="max-width: 100%;" />
          </div>
          <div class="cropper-controls">
            <button @click="rotateLeft" class="btn btn-outline-secondary">
              <i class="material-icons">rotate_left</i>
            </button>
            <button @click="rotateRight" class="btn btn-outline-secondary">
              <i class="material-icons">rotate_right</i>
            </button>
            <button @click="acceptCropped" class="btn btn-primary">
              <i class="material-icons">crop</i> Appliquer
            </button>
            <button @click="cancelCrop" class="btn btn-outline-secondary">
              <i class="material-icons">arrow_back</i> Retour
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import Cropper from 'cropperjs'

// Props
const props = defineProps({
  show: Boolean,
  title: {
    type: String,
    default: 'Photo de l\'agent'
  },
  agentId: [Number, String]
})

// Emits
const emit = defineEmits(['close', 'photo-updated'])

// Références aux éléments DOM
const fileInput = ref(null)
const videoElement = ref(null)
const cropperImage = ref(null)
const captureCanvas = ref(null)
const previewImage = ref(null)

// Instance de cropper
let cropperInstance = null

// State
const showCamera = ref(false)
const showCropper = ref(false)
const cameraCaptured = ref(false)
const selectedImage = ref('')  // Pour stocker l'URL de l'image sélectionnée/capturée
const stream = ref(null)

// Methods
const triggerFileInput = () => {
  fileInput.value.click()
}

const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  // Vérifier que c'est une image
  if (!file.type.startsWith('image/')) {
    alert('Veuillez sélectionner un fichier image')
    event.target.value = ''
    return
  }
  
  // Vérifier la taille du fichier (max 5 MB)
  const maxSize = 5 * 1024 * 1024 // 5 MB en octets
  if (file.size > maxSize) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
    alert(`La photo est trop grande (${fileSizeMB} MB). La taille maximale autorisée est de 5 MB. Veuillez sélectionner une image plus petite.`)
    event.target.value = ''
    return
  }
  
  // Préparer l'image pour le rognage
  const reader = new FileReader()
  reader.onload = (e) => {
    selectedImage.value = e.target.result
    showCropper.value = true
  }
  reader.readAsDataURL(file)
}

const startCamera = async () => {
  try {
    const constraints = {
      video: {
        width: 640,
        height: 480,
        facingMode: 'user'
      }
    }
    
    // Demande d'accès à la caméra
    stream.value = await navigator.mediaDevices.getUserMedia(constraints)
    videoElement.value.srcObject = stream.value
    videoElement.value.play()
    showCamera.value = true
  } catch (err) {
    console.error('Erreur accès caméra:', err)
    alert('Impossible d\'accéder à la caméra. Veuillez vérifier vos permissions.')
  }
}

const capturePhoto = () => {
  if (!videoElement.value || !captureCanvas.value) return
  
  const context = captureCanvas.value.getContext('2d')
  captureCanvas.value.width = videoElement.value.videoWidth
  captureCanvas.value.height = videoElement.value.videoHeight
  context.drawImage(videoElement.value, 0, 0, captureCanvas.value.width, captureCanvas.value.height)
  
  // Récupérer l'image comme base64
  const imageData = captureCanvas.value.toDataURL('image/png')
  
  // Préparer pour le rognage
  selectedImage.value = imageData
  showCamera.value = false
  showCropper.value = true
  cameraCaptured.value = true
  
  // Arrêter la caméra
  stopWebcam()
}

const retakePhoto = () => {
  cameraCaptured.value = false
}

const acceptCaptured = () => {
  if (!captureCanvas.value) return
  
  // Préparer l'image capturée pour le rognage
  cropperImage.value = captureCanvas.value.toDataURL('image/png')
  showCamera.value = false
  showCropper.value = true
  stopWebcam()
}

const cancelCamera = () => {
  showCamera.value = false
  stopWebcam()
}

const rotateLeft = () => {
  if (cropperInstance) {
    cropperInstance.rotate(-90)
  }
}

const rotateRight = () => {
  if (cropperInstance) {
    cropperInstance.rotate(90)
  }
}

const acceptCropped = () => {
  if (cropperInstance) {
    // Obtenir l'image ronnée avec validation du format portrait
    const croppedCanvas = cropperInstance.getCroppedCanvas({
      width: 300,
      height: 400,
      minWidth: 100,
      minHeight: 100,
      maxWidth: 4096,
      maxHeight: 4096,
      fillColor: '#fff',
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })
    
    // Vérifier les proportions
    const width = croppedCanvas.width
    const height = croppedCanvas.height
    const ratio = width / height
  
    if (ratio > 0.85) {
      alert('L\'image n\'est pas au format portrait. Veuillez recadrer pour obtenir un format portrait.')
      return
    }
    
    // Convertir en base64 pour le stockage
    const base64data = croppedCanvas.toDataURL('image/jpeg', 0.8)
    
    // Émettre l'événement avec la photo mise à jour
    emit('photo-updated', {
      agentId: props.agentId,
      photoData: base64data
    })
    
    // Fermer le modal
    emit('close')
  }
}

const cancelCrop = () => {
  showCropper.value = false
  
  // Si on vient de la caméra, revenir à la sélection
  if (showCamera.value) {
    showCamera.value = false
    stopWebcam()
  }
}

const stopWebcam = () => {
  if (stream.value) {
    stream.value.getTracks().forEach(track => track.stop())
    stream.value = null
  }
}

// Nettoyage quand le composant est démonté
onUnmounted(() => {
  stopWebcam()
})

// Initialiser Cropper quand l'image est prête à être rognée
const initCropper = () => {
  // Détruire l'instance existante si elle existe
  if (cropperInstance) {
    cropperInstance.destroy()
    cropperInstance = null
  }
  
  // Attendre que l'image soit chargée
  nextTick(() => {
    if (cropperImage.value && showCropper.value) {
      cropperInstance = new Cropper(cropperImage.value, {
        aspectRatio: 3 / 4,  // Format portrait
        viewMode: 1,         // Limite l'image dans le cadre
        guides: true,        // Afficher les guides
        center: true,        // Afficher le point central
        highlight: true,     // Zone à rogner surlignée
        background: false,   // Afficher un fond quadrillé
        autoCropArea: 0.8,   // Taille de la zone de découpe par défaut
        responsive: true,    // Redimensionnement responsive
        zoomable: true,      // Permettre le zoom
        movable: true        // Permettre le déplacement
      })
    }
  })
}

// Observer les changements de l'image et du mode cropper
watch([() => showCropper.value, () => selectedImage.value], ([newShowCropper, newImage]) => {
  if (newShowCropper && newImage) {
    // L'image est prête à être rognée, initialiser Cropper
    initCropper()
  } else {
    // Détruire l'instance si on quitte le mode rognage
    if (cropperInstance) {
      cropperInstance.destroy()
      cropperInstance = null
    }
  }
})

// Observer les changements du modal
watch(() => props.show, (newVal) => {
  if (!newVal) {
    showCamera.value = false
    showCropper.value = false
    cameraCaptured.value = false
    selectedImage.value = ''
    stopWebcam()
    
    // Détruire l'instance Cropper si elle existe
    if (cropperInstance) {
      cropperInstance.destroy()
      cropperInstance = null
    }
  }
})
</script>

<style scoped>
/* Styles nécessaires pour Cropper.js */
.cropper-container {
  direction: ltr;
  font-size: 0;
  line-height: 0;
  position: relative;
  touch-action: none;
  user-select: none;
}

.cropper-wrap-box,
.cropper-canvas,
.cropper-drag-box,
.cropper-crop-box,
.cropper-modal {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.cropper-wrap-box,
.cropper-canvas {
  overflow: hidden;
}

.cropper-drag-box {
  background-color: #fff;
  opacity: 0;
}

.cropper-modal {
  background-color: #000;
  opacity: 0.5;
}

.cropper-view-box {
  display: block;
  height: 100%;
  outline: 1px solid #39f;
  outline-color: rgba(51, 153, 255, 0.75);
  overflow: hidden;
  width: 100%;
}

.cropper-dashed {
  border: 0 dashed #eee;
  display: block;
  opacity: 0.5;
  position: absolute;
}

.cropper-dashed.dashed-h {
  border-bottom-width: 1px;
  border-top-width: 1px;
  height: calc(100% / 3);
  left: 0;
  top: calc(100% / 3);
  width: 100%;
}

.cropper-dashed.dashed-v {
  border-left-width: 1px;
  border-right-width: 1px;
  height: 100%;
  left: calc(100% / 3);
  top: 0;
  width: calc(100% / 3);
}

.cropper-center {
  display: block;
  height: 0;
  left: 50%;
  opacity: 0.75;
  position: absolute;
  top: 50%;
  width: 0;
}

.cropper-center::before,
.cropper-center::after {
  background-color: #eee;
  content: ' ';
  display: block;
  position: absolute;
}

.cropper-center::before {
  height: 1px;
  left: -3px;
  top: 0;
  width: 7px;
}

.cropper-center::after {
  height: 7px;
  left: 0;
  top: -3px;
  width: 1px;
}

.cropper-face,
.cropper-line,
.cropper-point {
  display: block;
  height: 100%;
  opacity: 0.1;
  position: absolute;
  width: 100%;
}

.cropper-face {
  background-color: #fff;
  left: 0;
  top: 0;
}

.cropper-line {
  background-color: #39f;
}

.cropper-line.line-e {
  cursor: ew-resize;
  right: -3px;
  top: 0;
  width: 5px;
}

.cropper-line.line-n {
  cursor: ns-resize;
  height: 5px;
  left: 0;
  top: -3px;
}

.cropper-line.line-w {
  cursor: ew-resize;
  left: -3px;
  top: 0;
  width: 5px;
}

.cropper-line.line-s {
  bottom: -3px;
  cursor: ns-resize;
  height: 5px;
  left: 0;
}

.cropper-point {
  background-color: #39f;
  height: 5px;
  opacity: 0.75;
  width: 5px;
}

.cropper-point.point-e {
  cursor: ew-resize;
  margin-top: -3px;
  right: -3px;
  top: 50%;
}

.cropper-point.point-n {
  cursor: ns-resize;
  left: 50%;
  margin-left: -3px;
  top: -3px;
}

.cropper-point.point-w {
  cursor: ew-resize;
  left: -3px;
  margin-top: -3px;
  top: 50%;
}

.cropper-point.point-s {
  bottom: -3px;
  cursor: s-resize;
  left: 50%;
  margin-left: -3px;
}

.cropper-point.point-ne {
  cursor: nesw-resize;
  right: -3px;
  top: -3px;
}

.cropper-point.point-nw {
  cursor: nwse-resize;
  left: -3px;
  top: -3px;
}

.cropper-point.point-sw {
  bottom: -3px;
  cursor: nesw-resize;
  left: -3px;
}

.cropper-point.point-se {
  bottom: -3px;
  cursor: nwse-resize;
  height: 20px;
  opacity: 1;
  right: -3px;
  width: 20px;
}

@media (min-width: 768px) {
  .cropper-point.point-se {
    height: 15px;
    width: 15px;
  }
}

@media (min-width: 992px) {
  .cropper-point.point-se {
    height: 10px;
    width: 10px;
  }
}

@media (min-width: 1200px) {
  .cropper-point.point-se {
    height: 5px;
    width: 5px;
    opacity: 0.75;
  }
}

.cropper-point.point-se::before {
  background-color: #39f;
  bottom: -50%;
  content: ' ';
  display: block;
  height: 200%;
  opacity: 0;
  position: absolute;
  right: -50%;
  width: 200%;
}

.cropper-invisible {
  opacity: 0;
}

.cropper-bg {
  background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQAQMAAAAlPW0iAAAAA3NCSVQICAjb4U/gAAAABlBMVEXMzMz////TjRV2AAAACXBIWXMAAArrAAAK6wGCiw1aAAAAHHRFWHRTb2Z0d2FyZQBBZG9iZSBGaXJld29ya3MgQ1M26LyyjAAAABFJREFUCJlj+M/AgBVhF/0PAH6/D/HkDxOGAAAAAElFTkSuQmCC');
}

.cropper-hide {
  display: block;
  height: 0;
  position: absolute;
  width: 0;
}

.cropper-hidden {
  display: none !important;
}

.cropper-move {
  cursor: move;
}

.cropper-crop {
  cursor: crosshair;
}

.cropper-disabled .cropper-drag-box,
.cropper-disabled .cropper-face,
.cropper-disabled .cropper-line,
.cropper-disabled .cropper-point {
  cursor: not-allowed;
}

.photo-capture-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 15px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h5 {
  margin: 0;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.close-btn:hover {
  color: #212529;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
}

.mode-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
  padding: 30px 0;
}

.mode-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  width: 180px;
  transition: all 0.3s;
}

.mode-btn i {
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.camera-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.camera-container video,
.captured-preview {
  width: 100%;
  max-height: 50vh;
  background-color: black;
  border-radius: 4px;
  margin-bottom: 20px;
}

.camera-controls,
.cropper-controls {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 15px;
}

.cropper-container {
  height: 400px;
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
  }
  
  .mode-btn {
    width: 140px;
    padding: 15px;
  }
}
</style>
